import type { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

export interface FieldRowProps {
  /** Field label (left column). */
  label: ReactNode;
  /** Optional supporting description under the label. */
  description?: ReactNode;
  /** Vertically align the control to the top (useful for multi-line controls). */
  alignTop?: boolean;
  /** The control (right column). */
  children: ReactNode;
}

/**
 * Two-column settings row: a label + description on the left and a control on
 * the right, collapsing to a single column on small screens.
 */
export function FieldRow({ label, description, alignTop = false, children }: FieldRowProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 260px) minmax(0, 1fr)' },
        gap: { xs: 1, md: 3 },
        alignItems: alignTop ? 'flex-start' : 'center',
        py: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
